import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, SelectInput } from "@pericles/framework-frontend";

import { fetchJarvisBench, startJarvisBench } from "../api/jarvisBenchClient";
import type { JarvisBenchState, JarvisBenchSummary } from "../types/jarvis";
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";
import { useOpenAiSecretStatus } from "../hooks/useOpenAiSecretStatus";

const JARVIS_ENVIRONMENTS = [
  { value: "sandbox", label: "Sandbox" },
  { value: "staging", label: "Staging" },
  { value: "dev", label: "Dev" },
];

const JARVIS_PROVIDERS = [
  { value: "stub", label: "Stub (no patches)" },
  { value: "rules", label: "Rules (deterministic)" },
  { value: "openai", label: "OpenAI (provide API key)" },
];

function formatRate(value?: number): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "-";
  return `${(value * 100).toFixed(1)}%`;
}

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function buildStatusBadge(state: JarvisBenchState | null): { label: string; variant: "default" | "success" | "warning" | "danger" } {
  if (!state) return { label: "Idle", variant: "default" };
  switch (state.status) {
    case "running":
      return { label: "Running", variant: "warning" };
    case "succeeded":
      return { label: "Succeeded", variant: "success" };
    case "failed":
      return { label: "Failed", variant: "danger" };
    default:
      return { label: "Idle", variant: "default" };
  }
}

export function ProjectJarvisBenchTab(): JSX.Element {
  const [benchState, setBenchState] = useState<JarvisBenchState | null>(null);
  const [benchReport, setBenchReport] = useState<{ summary: JarvisBenchSummary | null; version?: number; generatedAt?: string } | null>(null);
  const [logTail, setLogTail] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<string>(JARVIS_ENVIRONMENTS[0]?.value ?? "sandbox");
  const [provider, setProvider] = useState<string>("stub");
  const [isLoading, setIsLoading] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { status: openAiStatus, isLoading: secretsLoading, error: secretError, refreshStatus } = useOpenAiSecretStatus();

  const loadBench = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = await fetchJarvisBench();
      setBenchState(payload.state ?? null);
      setBenchReport(payload.report ?? null);
      setLogTail(payload.logTail ?? null);
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : "Failed to load Jarvis bench status";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBench();
  }, [loadBench]);

  const latestSummary = useMemo(() => benchReport?.summary ?? null, [benchReport]);
  const statusBadge = useMemo(() => buildStatusBadge(benchState), [benchState]);

  const handleStartBench = async () => {
    setError(null);
    if (provider === "openai" && openAiStatus && !openAiStatus.configured) {
      setError("OPENAI_API_KEY missing. Add it in Settings → Workspace settings → Secrets or set it server-side.");
      return;
    }
    const environmentLabel = JARVIS_ENVIRONMENTS.find((entry) => entry.value === environment)?.label ?? environment;
    const confirmed = window.confirm(
      `Launch Jarvis bench in ${environmentLabel} with provider ${provider}?\nProd/prod-like environments are blocked. Logs are redacted.`,
    );
    if (!confirmed) return;

    setIsLaunching(true);
    try {
      const state = await startJarvisBench({ environment, provider, workspaceId: DEFAULT_WORKSPACE_ID });
      setBenchState(state);
      await loadBench();
      if (provider === "openai") {
        void refreshStatus();
      }
    } catch (launchError) {
      const message = launchError instanceof Error ? launchError.message : "Failed to start Jarvis bench";
      setError(message);
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">Jarvis bench</div>
              <p className="text-sm text-slate-700">Run `pnpm jarvis:bench` with guardrails and view the latest report.</p>
            </div>
            <button
              type="button"
              className="button button-ghost button-sm"
              onClick={() => void loadBench()}
              disabled={isLoading}
            >
              {isLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {error ? <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
          {secretError ? <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{secretError}</div> : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">Launch bench</div>
                <Badge label={statusBadge.label} variant={statusBadge.variant} />
              </div>

              <div className="mt-3 space-y-3 text-sm text-slate-800">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Environment</label>
                  <SelectInput value={environment} onChange={(event) => setEnvironment(event.target.value)}>
                    {JARVIS_ENVIRONMENTS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                  <p className="text-xs text-slate-600">Prod and prod-like targets are blocked.</p>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Provider</label>
                  <SelectInput value={provider} onChange={(event) => setProvider(event.target.value)}>
                    {JARVIS_PROVIDERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                  {provider === "openai" ? (
                    <div className="flex flex-col gap-1 text-xs text-slate-700">
                      <div className="flex items-center gap-2">
                        <span>OpenAI API key status:</span>
                        <Badge
                          label={openAiStatus?.configured ? "Configured" : "Missing"}
                          variant={openAiStatus?.configured ? "success" : "danger"}
                        />
                      </div>
                      {openAiStatus?.previewMasked ? (
                        <div className="text-[11px] text-slate-600">Using secret/env ({openAiStatus.previewMasked}).</div>
                      ) : (
                        <div className="text-[11px] text-slate-600">
                          Add a key in Settings → Workspace settings → Secrets. Provider launch will fail if missing.
                        </div>
                      )}
                      <button
                        type="button"
                        className="button button-ghost button-xs self-start"
                        onClick={() => void refreshStatus()}
                        disabled={secretsLoading}
                      >
                        {secretsLoading ? "Checking..." : "Refresh status"}
                      </button>
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="button button-primary"
                  disabled={isLaunching || isLoading || benchState?.status === "running"}
                  onClick={() => void handleStartBench()}
                >
                  {isLaunching ? "Starting bench..." : "Start bench"}
                </button>
                <div className="text-xs text-slate-600">
                  Bench runs stream redacted, truncated log tails only. Download the report JSON for full details.
                </div>
              </div>
            </div>

            <div className="rounded border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">Latest report</div>
                {benchState?.reportUrl ? (
                  <a className="text-sm text-blue-700 underline" href={benchState.reportUrl}>
                    Download latest report
                  </a>
                ) : null}
              </div>

              <div className="mt-2 grid gap-2 text-sm text-slate-800 md:grid-cols-2">
                <div>Success rate: {formatRate(latestSummary?.successRate)}</div>
                <div>Mean iterations: {latestSummary?.meanIterations ?? "-"}</div>
                <div>Active scenarios: {latestSummary?.activeScenarios ?? "-"}</div>
                <div>Quarantined: {latestSummary?.quarantinedScenarios ?? 0}</div>
                <div>Provider: {benchState?.provider ?? "-"}</div>
                <div>Environment: {benchState?.environment ?? "-"}</div>
                <div>Started: {formatDateTime(benchState?.startedAt)}</div>
                <div>Finished: {formatDateTime(benchState?.finishedAt)}</div>
                {benchReport?.version ? <div>Report version: v{benchReport.version}</div> : null}
                {benchReport?.generatedAt ? <div>Generated: {formatDateTime(benchReport.generatedAt)}</div> : null}
              </div>

              <div className="mt-3 text-sm text-slate-800">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Top failure signatures</div>
                {latestSummary?.topFailureSignatures?.length ? (
                  <ul className="mt-1 space-y-1 text-sm">
                    {latestSummary.topFailureSignatures.map((entry) => (
                      <li key={entry.signature} className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1">
                        <span className="font-mono text-[12px] text-slate-800">{entry.signature}</span>
                        <span className="text-xs text-slate-600">{entry.count}x</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-600">No failures recorded in the latest report.</div>
                )}
              </div>

              <div className="mt-3 rounded bg-slate-900 p-2 text-[11px] font-mono text-slate-100">
                <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-300">Bench log tail (redacted)</div>
                {logTail ? (
                  <pre className="whitespace-pre-wrap break-words text-[11px] leading-4">{logTail}</pre>
                ) : (
                  <div className="text-[11px] text-slate-400">No bench logs captured yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
